import type { RelationshipInvestigationConfig } from './RelationshipInvestigationConfig.js';

export type RelationshipInvestigationPhase =
  | 'observe'
  | 'predict'
  | 'investigate'
  | 'collect-evidence'
  | 'compare'
  | 'apply'
  | 'complete';

export interface RelationshipComparisonResult {
  readonly predictionSupported: boolean;
  readonly selectedConditionId: string;
  readonly evidenceSupportsCondition: boolean;
  readonly supportedConditionId: string;
  readonly evidenceStatement: string;
}

export interface RelationshipInvestigationSnapshot {
  readonly investigationId: string;
  readonly phase: RelationshipInvestigationPhase;
  readonly predictionConditionId?: string;
  readonly observedInteractionIds: readonly string[];
  readonly collectedEvidenceIds: readonly string[];
  readonly comparison?: RelationshipComparisonResult;
}

const PHASES: readonly RelationshipInvestigationPhase[] = [
  'observe',
  'predict',
  'investigate',
  'collect-evidence',
  'compare',
  'apply',
  'complete',
];

export class RelationshipInvestigationState {
  private phaseValue: RelationshipInvestigationPhase = 'observe';
  private predictionConditionIdValue?: string;
  private readonly observedInteractionIds = new Set<string>();
  private readonly collectedEvidenceIds = new Set<string>();
  private comparisonValue?: RelationshipComparisonResult;

  constructor(
    readonly config: RelationshipInvestigationConfig,
    initialSnapshot?: RelationshipInvestigationSnapshot,
  ) {
    if (initialSnapshot) this.restore(initialSnapshot);
  }

  get phase() { return this.phaseValue; }
  get isComplete() { return this.phaseValue === 'complete'; }
  get readyToCollectEvidence() {
    return this.config.requiredInteractionIds.every(id => this.observedInteractionIds.has(id));
  }
  get readyToCompare() {
    return this.config.requiredEvidenceIds.every(id => this.collectedEvidenceIds.has(id));
  }

  snapshot(): RelationshipInvestigationSnapshot {
    return {
      investigationId: this.config.id,
      phase: this.phaseValue,
      predictionConditionId: this.predictionConditionIdValue,
      observedInteractionIds: [...this.observedInteractionIds],
      collectedEvidenceIds: [...this.collectedEvidenceIds],
      comparison: this.comparisonValue,
    };
  }

  acknowledgeObservation() {
    if (this.phaseValue !== 'observe') return false;
    this.phaseValue = 'predict';
    return true;
  }

  choosePrediction(conditionId: string) {
    if (this.phaseValue !== 'predict') return false;
    if (!this.config.conditions.some(condition => condition.id === conditionId)) return false;
    this.predictionConditionIdValue = conditionId;
    this.phaseValue = 'investigate';
    return true;
  }

  recordInteraction(interactionId: string) {
    if (this.phaseValue !== 'investigate') return false;
    if (!this.config.interactions.some(interaction => interaction.id === interactionId)) return false;
    if (this.observedInteractionIds.has(interactionId)) return false;
    this.observedInteractionIds.add(interactionId);
    return true;
  }

  beginEvidenceCollection() {
    if (this.phaseValue !== 'investigate' || !this.readyToCollectEvidence) return false;
    this.phaseValue = 'collect-evidence';
    return true;
  }

  collectEvidence(evidenceId: string) {
    if (this.phaseValue !== 'collect-evidence') return false;
    const evidence = this.config.evidenceTargets.find(target => target.id === evidenceId);
    if (!evidence || this.collectedEvidenceIds.has(evidenceId)) return false;
    if (!evidence.sourceInteractionIds.every(id => this.observedInteractionIds.has(id))) return false;
    this.collectedEvidenceIds.add(evidenceId);
    if (this.readyToCompare) this.phaseValue = 'compare';
    return true;
  }

  compareEvidence(conditionId: string): RelationshipComparisonResult | undefined {
    if (this.phaseValue !== 'compare' || !this.predictionConditionIdValue) return undefined;
    if (!this.config.conditions.some(condition => condition.id === conditionId)) return undefined;
    const evidenceSupportsCondition = conditionId === this.config.supportedConditionId;
    this.comparisonValue = {
      predictionSupported: this.predictionConditionIdValue === this.config.supportedConditionId,
      selectedConditionId: conditionId,
      evidenceSupportsCondition,
      supportedConditionId: this.config.supportedConditionId,
      evidenceStatement: evidenceSupportsCondition
        ? this.config.evidenceStatement
        : this.config.unsupportedComparisonFeedback,
    };
    if (evidenceSupportsCondition) this.phaseValue = 'apply';
    return this.comparisonValue;
  }

  applyFinding() {
    if (this.phaseValue !== 'apply' || !this.comparisonValue) return false;
    this.phaseValue = 'complete';
    return true;
  }

  reset() {
    this.phaseValue = 'observe';
    this.predictionConditionIdValue = undefined;
    this.observedInteractionIds.clear();
    this.collectedEvidenceIds.clear();
    this.comparisonValue = undefined;
  }

  private restore(snapshot: RelationshipInvestigationSnapshot) {
    if (snapshot.investigationId !== this.config.id || !PHASES.includes(snapshot.phase)) return;
    const conditionIds = new Set(this.config.conditions.map(condition => condition.id));
    const interactionIds = new Set(this.config.interactions.map(interaction => interaction.id));
    const evidenceIds = new Set(this.config.evidenceTargets.map(evidence => evidence.id));
    const prediction = snapshot.predictionConditionId;
    if (prediction && conditionIds.has(prediction)) this.predictionConditionIdValue = prediction;
    snapshot.observedInteractionIds.forEach(id => {
      if (interactionIds.has(id)) this.observedInteractionIds.add(id);
    });
    snapshot.collectedEvidenceIds.forEach(id => {
      if (evidenceIds.has(id)) this.collectedEvidenceIds.add(id);
    });

    const phaseNeedsPrediction = !['observe', 'predict'].includes(snapshot.phase);
    const phaseNeedsInteractions = ['collect-evidence', 'compare', 'apply', 'complete'].includes(snapshot.phase);
    const phaseNeedsEvidence = ['compare', 'apply', 'complete'].includes(snapshot.phase);
    const phaseNeedsComparison = ['apply', 'complete'].includes(snapshot.phase);
    if (phaseNeedsPrediction && !this.predictionConditionIdValue) return this.reset();
    if (phaseNeedsInteractions && !this.readyToCollectEvidence) return this.reset();
    if (phaseNeedsEvidence && !this.readyToCompare) return this.reset();
    if (phaseNeedsComparison && !snapshot.comparison) return this.reset();
    this.comparisonValue = snapshot.comparison;
    this.phaseValue = snapshot.phase;
  }
}
